import { useMemo } from "react";
import { ReactFlow, Background, Controls, type NodeMouseHandler } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { StepNode } from "./StepNode";
import { baueGraph } from "./graphLayout";
import type { CaseStepFold } from "@/types";

// #303 (UC-3): der Ablauf als Bild, mit dem Stand des Laufs darüber gelegt.
//
// ⚠ Die Knoten kommen aus der STEP-FALTUNG des Laufs (`GET /cases/:id/steps`), NICHT aus
// einem Lesen der Vorlage: die Faltung trägt Reihenfolge, Klarnamen und Zustand bereits,
// und sie kommt aus unserer Datenbank. Ein Datei-Zugriff auf die Ablage in der Oberfläche
// hätte eine zweite Wahrheit über den Ablauf eröffnet (und eine zweite Fehlerquelle,
// sobald die Ablage klemmt).
//
// ⚠ Es gibt DESHALB keine Struktur-Ansicht auf der Vorlagen-Seite: kein UI-erreichbarer
// Endpoint liefert die Schritt-Liste einer Vorlage (`GET /processes/:id` liefert
// `ProcessResponse` ohne Schritte, die Konfigurations-Sicht der Einrichtung schliesst sie
// ausdrücklich aus). Sie wäre nur über den rohen Datei-Zugriff auf die `process.json` zu
// haben — anderes Recht, andere Wahrheit. Das Ticket erlaubt die Vorlagen-Ansicht nur,
// „wenn sie billig ist"; sie ist es nicht.

const nodeTypes = { schritt: StepNode };

export function ProcessGraphViewer({
  steps,
  currentStepId,
  onStepClick,
}: {
  steps: readonly CaseStepFold[];
  currentStepId: string | null;
  onStepClick?: (stepId: string) => void;
}) {
  const { nodes, edges } = useMemo(() => baueGraph(steps, currentStepId), [steps, currentStepId]);
  const onNodeClick: NodeMouseHandler = (_e, node) => onStepClick?.(node.id);

  if (steps.length === 0) return null;

  return (
    <div
      className="h-[190px] w-full overflow-hidden rounded-lg border border-border bg-surface"
      data-testid="process-graph"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.2}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="var(--border-strong)" gap={22} />
        <Controls
          className="!rounded-md !border !border-border !bg-surface !shadow-sm"
          showInteractive={false}
        />
      </ReactFlow>
    </div>
  );
}
